<template>
  <div class="lower-limb-orthosis-step-one">
    <!-- Draw view -->
    <draw-view
      v-if="showDrawing"
      v-model="form.blob_cut_out"
      :background-image="drawingImage"
      @on-save="showDrawing = false"
    />

    <template v-if="!showDrawing">
      <v-layout justify-space-between>
        <v-flex>
          <v-layout
            justify-center
            class="mb-0"
          >
            <v-checkbox
              v-model="form.idc_right"
              class="mb-0"
              hide-details
              label="DROITE"
            />
            <v-checkbox
              v-model="form.idc_left"
              class="mb-0 ml-5"
              hide-details
              label="GAUCHE"
            />
          </v-layout>
        </v-flex>
      </v-layout>

      <v-layout justify-center>
        <v-radio-group
          v-model="form.type"
          class="mt-1"
          hide-details
          row
        >
          <v-radio
            label="Coque talonière"
            value="HEEL_SHELL"
          />
          <v-radio
            label="Attelle de jour"
            value="DAY_SPLINT"
          />
          <v-radio
            label="Attelle de nuit"
            value="NIGHT_SPLINT"
          />
          <v-radio
            label="Sarmiento"
            value="SARMIENTO"
          />
          <v-radio
            label="Genouillere"
            value="KNEE_BRACE"
          />
        </v-radio-group>
      </v-layout>

      <v-layout
        justify-space-between
        hide-details
        class="mb-4 mt-8"
      >
        <v-flex sm4>
          <v-btn
            block
            @click="$emit('short-schematic')"
          >
            Schéma courte
          </v-btn>
        </v-flex>
        <v-flex sm4>
          <v-btn
            block
            @click="$emit('all-leg-schematic')"
          >
            Schéma jambe entiere
          </v-btn>
        </v-flex>
        <v-flex sm4>
          <v-btn
            block
            @click="$emit('show-drawing')"
          >
            Schéma diff vues
          </v-btn>
        </v-flex>
      </v-layout>

      <v-layout
        justify-space-between
        mt-4
      >
        <v-flex sm6>
          <v-switch
            v-model="form.idc_exterior_finishing"
            hide-details
            label="Finition Extérieure :"
            class="title mb-2"
            @change="clearExtFinishingFields"
          />

          <v-layout justify-space-between>
            <v-flex sm6>
              <v-layout>
                <v-radio-group
                  v-model="form.exterior_finishing_type"
                  class="mt-1"
                  hide-details
                  row
                  :disabled="!form.idc_exterior_finishing"
                >
                  <v-radio
                    label="PE"
                    value="PE"
                  />
                  <v-radio
                    label="PP"
                    value="PP"
                  />
                </v-radio-group>
              </v-layout>
            </v-flex>
            <v-flex sm6>
              <v-text-field
                v-model="form.pp"
                v-validate="'numeric|max:4'"
                data-vv-name="(Finition Ext) PP/PE"
                :class="{ error: errors.has('(Finition Ext) PP/PE') }"
                class="mt-1 mb-2"
                label="mm"
                type="number"
                :disabled="!form.idc_exterior_finishing"
                outlined
                hide-details
              />
            </v-flex>
          </v-layout>

          <v-flex>
            <v-text-field
              v-model="form.polystone"
              v-validate="'numeric|max:4'"
              data-vv-name="(Finition Ext) Polystone"
              :class="{ error: errors.has('(Finition Ext) Polystone') }"
              class="mb-1 mb-2"
              label="POLYSTONE mm"
              type="number"
              :disabled="!form.idc_exterior_finishing"
              outlined
              hide-details
            />
          </v-flex>

          <v-layout>
            <v-flex>
              <v-text-field
                v-model="form.exterior_finishing_other"
                class="mb-3"
                label="AUTRE"
                hide-details
                maxlength="20"
                outlined
                :disabled="!form.idc_exterior_finishing"
              />
            </v-flex>
          </v-layout>

          <v-layout class="color-select">
            <v-select
              v-model="form.transfer_id"
              :items="transferColors"
              item-text="label"
              item-value="id"
              hide-details
              label="Transfert :"
              filled
              clearable
              :disabled="!form.idc_exterior_finishing"
            />
            <template v-if="previewTransfer">
              <v-flex
                v-if="previewTransfer.texture == 'TEXTURE'"
                class="preview"
                style="width: 35px; height: 35px"
              >
                <img
                  :src="previewTransfer.image"
                  style="width: 100%; height:100%"
                  alt
                >
              </v-flex>
              <v-flex
                v-else-if="previewTransfer.texture == 'COLOR'"
                class="preview"
                :style="{ 'background-color': '#' + previewTransfer.rgb_code }"
              />
            </template>
          </v-layout>
        </v-flex>

        <v-flex sm6>
          <v-switch
            v-model="form.idc_interior_finishing"
            hide-details
            label="Finition Intérieure :"
            class="title"
            @change="clearIntFinishingFields"
          />
          <v-layout wrap>
            <v-radio-group
              v-model="form.interior_finishing_type"
              class="mt-1"
              hide-details
              row
              :disabled="!form.idc_interior_finishing"
            >
              <v-radio
                label="PLASTA"
                value="PLASTA"
              />
              <v-radio
                label="EVA"
                value="EVA"
              />
            </v-radio-group>

            <v-flex>
              <v-layout class="ml-4 mt-3 mb-2">
                <v-text-field
                  v-model="form.interior_finishing_other"
                  label="AUTRE"
                  hide-details
                  maxlength="20"
                  outlined
                  :disabled="!form.idc_interior_finishing"
                />
              </v-layout>
            </v-flex>
          </v-layout>

          <v-layout>
            <v-flex>
              <v-text-field
                v-model="form.thickness"
                v-validate="'numeric|max:4'"
                data-vv-name="(Finition Int) Epaisseur"
                :class="{ error: errors.has('(Finition Int) Epaisseur') }"
                class="mb-2"
                label="Épaisseur en mm"
                type="number"
                :disabled="!form.idc_interior_finishing"
                outlined
                hide-details
              />
            </v-flex>
          </v-layout>

          <v-flex>
            <v-layout>
              <v-flex>
                <v-text-field
                  v-model="form.skin"
                  class="mb-3"
                  label="PEAU"
                  maxlength="20"
                  hide-details
                  outlined
                  :disabled="!form.idc_interior_finishing"
                />
              </v-flex>
            </v-layout>
          </v-flex>
          <v-layout
            mb-8
            class="color-select"
          >
            <v-select
              v-model="form.thermo_color_id"
              :items="thermoColors"
              item-text="label"
              item-value="id"
              hide-details
              label="THERMO :"
              filled
              clearable
              :disabled="!form.idc_interior_finishing"
            />
            <template v-if="previewThermoColor">
              <v-flex
                v-if="previewThermoColor.texture == 'TEXTURE'"
                class="preview"
                style="width: 35px; height: 35px"
              >
                <img
                  :src="previewThermoColor.image"
                  style="width: 100%; height:100%"
                  alt
                >
              </v-flex>
              <v-flex
                v-else-if="previewThermoColor.texture == 'COLOR'"
                class="preview"
                :style="{
                  'background-color': '#' + previewThermoColor.rgb_code
                }"
              />
            </template>
          </v-layout>
        </v-flex>
      </v-layout>

      <v-layout
        mb-5
        justify-space-between
      >
        <v-flex
          mt-2
          sm2
        >
          <h2>ARTICULATION :</h2>
        </v-flex>
        <v-flex sm3>
          <v-text-field
            v-model="form.ankle"
            class="mb-3"
            label="CHEVILLE"
            hide-details
            outlined
          />
        </v-flex>
        <v-flex sm3>
          <v-text-field
            v-model="form.knee"
            class="mb-3"
            label="GENOU"
            hide-details
            outlined
          />
        </v-flex>
        <v-flex sm3>
          <v-text-field
            v-model="form.articulation_other"
            class="mb-3"
            label="AUTRE"
            maxlength="45"
            hide-details
            outlined
          />
        </v-flex>
      </v-layout>

      <v-layout justify-space-between>
        <v-flex
          column
          sm2
        >
          <h2>ADJONCTION :</h2>
          <v-checkbox
            v-model="form.idc_reminder"
            hide-details
            label="RAPPEL"
          />
          <v-checkbox
            v-model="form.idc_lock"
            hide-details
            label="VERROU"
          />
          <v-checkbox
            v-model="form.idc_slipper"
            hide-details
            label="CHAUSSON"
          />
          <v-checkbox
            v-model="form.idc_knee_sling"
            hide-details
            label="FRONDE GENOU"
          />
          <v-text-field
            v-model="form.addition_other"
            class="mt-6"
            label="AUTRE"
            maxlength="45"
            hide-details
            outlined
          />
        </v-flex>

        <v-flex sm10>
          <v-simple-table>
            <thead>
              <tr>
                <th class="text-center">
                  SANGLES
                </th>
                <th class="text-center">
                  Couleur
                </th>
                <th class="text-center">
                  Taille
                </th>
                <th class="text-center">
                  Type
                </th>
                <th class="text-center">
                  Coussin
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>S1</th>
                <td>
                  <v-layout align-center>
                    <v-select
                      label
                      filled
                      clearable
                      hide-details
                      :items="['BLANC', 'NOIR']"
                    />
                  </v-layout>
                </td>
                <td>
                  <v-select
                    v-model="form.s1_size"
                    :items="dropdowns.s1_size"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-select
                    v-model="form.s1_type"
                    :items="dropdowns.s1_type"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="form.s1_cushion"
                    maxlength="45"
                    hide-details
                    outlined
                    clearable
                  />
                </td>
              </tr>
              <tr>
                <th>S2</th>
                <td>
                  <v-layout align-center>
                    <v-select
                      label
                      filled
                      clearable
                      hide-details
                      :items="['BLANC', 'NOIR']"
                    />
                  </v-layout>
                </td>
                <td>
                  <v-select
                    v-model="form.s2_size"
                    :items="dropdowns.s2_size"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-select
                    v-model="form.s2_type"
                    :items="dropdowns.s2_type"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="form.s2_cushion"
                    maxlength="45"
                    hide-details
                    outlined
                    clearable
                  />
                </td>
              </tr>
              <tr>
                <th>S3</th>
                <td>
                  <v-layout align-center>
                    <v-select
                      label
                      filled
                      clearable
                      hide-details
                      :items="['BLANC', 'NOIR']"
                    />
                  </v-layout>
                </td>
                <td>
                  <v-select
                    v-model="form.s3_size"
                    :items="dropdowns.s3_size"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-select
                    v-model="form.s3_type"
                    :items="dropdowns.s3_type"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="form.s3_cushion"
                    maxlength="45"
                    hide-details
                    outlined
                    clearable
                  />
                </td>
              </tr>
              <tr>
                <th>S4</th>
                <td>
                  <v-layout align-center>
                    <v-select
                      label
                      filled
                      clearable
                      hide-details
                      :items="['BLANC', 'NOIR']"
                    />
                  </v-layout>
                </td>
                <td>
                  <v-select
                    v-model="form.s4_size"
                    :items="dropdowns.s4_size"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-select
                    v-model="form.s4_type"
                    :items="dropdowns.s4_type"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="form.s4_cushion"
                    maxlength="45"
                    hide-details
                    outlined
                    clearable
                  />
                </td>
              </tr>
              <tr>
                <th>S5</th>
                <td>
                  <v-layout align-center>
                    <v-select
                      label
                      filled
                      clearable
                      hide-details
                      :items="['BLANC', 'NOIR']"
                    />
                  </v-layout>
                </td>
                <td>
                  <v-select
                    v-model="form.s5_size"
                    :items="dropdowns.s5_size"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-select
                    v-model="form.s5_type"
                    :items="dropdowns.s5_type"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="form.s5_cushion"
                    maxlength="45"
                    hide-details
                    outlined
                    clearable
                  />
                </td>
              </tr>
              <tr>
                <th>S6</th>
                <td>
                  <v-layout align-center>
                    <v-select
                      label
                      filled
                      clearable
                      hide-details
                      :items="['BLANC', 'NOIR']"
                    />
                  </v-layout>
                </td>
                <td>
                  <v-select
                    v-model="form.s6_size"
                    :items="dropdowns.s6_size"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-select
                    v-model="form.s6_type"
                    :items="dropdowns.s6_type"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="form.s6_cushion"
                    maxlength="45"
                    hide-details
                    outlined
                    clearable
                  />
                </td>
              </tr>
              <tr>
                <th>S7</th>
                <td>
                  <v-layout align-center>
                    <v-select
                      label
                      filled
                      clearable
                      hide-details
                      :items="['BLANC', 'NOIR']"
                    />
                  </v-layout>
                </td>
                <td>
                  <v-select
                    v-model="form.s7_size"
                    :items="dropdowns.s7_size"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-select
                    v-model="form.s7_type"
                    :items="dropdowns.s7_type"
                    item-text="label"
                    item-value="value"
                    label
                    filled
                    clearable
                    hide-details
                  />
                </td>
                <td>
                  <v-text-field
                    v-model="form.s7_cushion"
                    maxlength="45"
                    hide-details
                    outlined
                    clearable
                  />
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-flex>
      </v-layout>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import DrawView from "@/views/components/DrawView";

export default {
  name: "LowerLimbOrthosisStepOne",
  inject: ["parentValidator"],
  components: {
    DrawView
  },
  props: {
    form: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    showDrawing: false
  }),
  computed: {
    ...mapGetters({
      transferColors: "colors/getTRANSFERType",
      thermoColors: "colors/getTHERMOType",

      dropdowns: "dropdowns/getLowerLimbOptions"
    }),

    // Returns transfer textures
    previewTransfer() {
      return this.transferColors.find(item => item.id == this.form.transfer_id);
    },

    previewThermoColor() {
      return this.thermoColors.find(
        color => color.id == this.form.thermo_color_id
      );
    },

    drawingImage() {
      return require("../../../../assets/img/schema_omi_3c.svg");
    }
  },
  created() {
    this.$validator = this.parentValidator;
  },
  methods: {
    // Clear exterior finishing fields
    clearExtFinishingFields() {
      this.form.exterior_finishing_type = null;
      this.form.pp = null;
      this.form.polystone = null;
      this.form.exterior_finishing_other = null;
      this.form.transfer_id = null;
    },

    // Clear interior finishing fields
    clearIntFinishingFields() {
      this.form.interior_finishing_type = null;
      this.form.interior_finishing_other = null;
      this.form.skin = null;
      this.form.thermo_color_id = null;
      this.form.thickness = null;
    }
  }
};
</script>

<style lang="scss">
.lower-limb-orthosis-step-one {
  .checkbox-input-container {
    position: relative;
    margin-top: 30px;
    .v-input {
      margin-top: 0;
    }
    .checkbox-input {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translateY(-50%);
      max-width: 80px !important;
      &.not-mm {
        max-width: 160px !important;
      }
    }
  }
}
</style>
